import type { CardsBlockContent } from "../types"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader } from "@/components/ui/card"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"

interface CardsBlockEditorProps {
  content: CardsBlockContent | null
  onChange: (content: CardsBlockContent) => void
}

export function CardsBlockEditor({ content, onChange }: CardsBlockEditorProps) {
  const c = content ?? {}
  const items = c.items ?? [{ title: "", body: "", cta_text: "", cta_url: "" }]
  const update = (partial: Partial<CardsBlockContent>) =>
    onChange({ ...c, ...partial })
  const setItem = (
    i: number,
    field: keyof NonNullable<CardsBlockContent["items"]>[number],
    val: string | undefined
  ) => {
    const next = [...items]
    if (!next[i]) next[i] = { title: "", body: "", cta_text: "", cta_url: "" }
    next[i] = { ...next[i], [field]: val }
    update({ items: next })
  }
  const addItem = () =>
    update({ items: [...items, { title: "", body: "", cta_text: "", cta_url: "" }] })
  const removeItem = (i: number) =>
    update({ items: items.filter((_, j) => j !== i) })

  return (
    <Card>
      <CardHeader>
        <h3 className="text-base font-medium">Cards</h3>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <Label>Heading</Label>
          <Input
            value={c.heading ?? ""}
            onChange={(e) => update({ heading: e.target.value })}
            placeholder="Section heading"
          />
        </div>
        <div>
          <Label>Subheading</Label>
          <Input
            value={c.subheading ?? ""}
            onChange={(e) => update({ subheading: e.target.value })}
            placeholder="Optional subheading"
          />
        </div>
        <div>
          <Label>Columns</Label>
          <Select
            value={String(c.columns ?? 3)}
            onValueChange={(v) => update({ columns: Number(v) as 1 | 2 | 3 })}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="1">1</SelectItem>
              <SelectItem value="2">2</SelectItem>
              <SelectItem value="3">3</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-3">
          <Label>Cards</Label>
          {items.map((item, i) => (
            <div key={i} className="rounded-md border p-3 space-y-2">
              <Input
                placeholder="Title"
                value={item.title ?? ""}
                onChange={(e) => setItem(i, "title", e.target.value)}
              />
              <Textarea
                placeholder="Body"
                value={item.body ?? ""}
                onChange={(e) => setItem(i, "body", e.target.value)}
                rows={2}
                className="resize-none"
              />
              <Input
                placeholder="Image URL (optional)"
                value={item.image_url ?? ""}
                onChange={(e) => setItem(i, "image_url", e.target.value ? e.target.value : undefined)}
              />
              <Input
                placeholder="CTA text"
                value={item.cta_text ?? ""}
                onChange={(e) => setItem(i, "cta_text", e.target.value)}
              />
              <Input
                placeholder="CTA URL"
                value={item.cta_url ?? ""}
                onChange={(e) => setItem(i, "cta_url", e.target.value)}
              />
              <Button type="button" variant="ghost" size="sm" onClick={() => removeItem(i)}>
                Remove
              </Button>
            </div>
          ))}
          <Button type="button" variant="outline" size="sm" onClick={addItem}>
            Add card
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}
